import { useCallback, useEffect, useState, type FormEvent } from "react";

// ── Types ─────────────────────────────────────────────────────

type TaskStatus =
  | "Backlog"
  | "InProgress"
  | "AwaitingReview"
  | "Approved"
  | "Done"
  | "Failed";

type Priority = "Low" | "Medium" | "High" | "Urgent";

type Assignee =
  | { Agent: string }
  | { Human: string }
  | "Unassigned";

type AgentTask = {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: Priority;
  assignee: Assignee;
  prompt: string;
  result: string | null;
  created_at: string;
  updated_at: string;
};

type Column = {
  status: TaskStatus;
  tasks: AgentTask[];
  count: number;
};

// ── Constants ─────────────────────────────────────────────────

const KANBAN_COLUMNS: { status: TaskStatus; label: string }[] = [
  { status: "Backlog", label: "Backlog" },
  { status: "InProgress", label: "In Progress" },
  { status: "AwaitingReview", label: "Awaiting Review" },
  { status: "Done", label: "Done" },
];

const PRIORITY_COLORS: Record<Priority, string> = {
  Low: "#6272a4",
  Medium: "#50fa7b",
  High: "#f1fa8c",
  Urgent: "#ff5555",
};

const PRIORITY_ORDER: Priority[] = ["Urgent", "High", "Medium", "Low"];

const STATUS_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  Backlog: ["InProgress"],
  InProgress: ["AwaitingReview", "Failed"],
  AwaitingReview: ["Approved", "InProgress"],
  Approved: ["Done", "InProgress"],
  Done: ["Backlog"],
  Failed: ["Backlog", "InProgress"],
};

// ── Helpers ───────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function assigneeLabel(a: Assignee): string {
  if (a === "Unassigned") return "unassigned";
  if (typeof a === "object" && "Agent" in a) return `agent:${a.Agent}`;
  if (typeof a === "object" && "Human" in a) return `human:${a.Human}`;
  return "unassigned";
}

function assigneeDisplay(a: Assignee): string {
  if (a === "Unassigned") return "unassigned";
  if (typeof a === "object" && "Agent" in a) return a.Agent;
  if (typeof a === "object" && "Human" in a) return a.Human;
  return "unassigned";
}

// ── Component ─────────────────────────────────────────────────

export function TaskBoardWorkbench() {
  const [columns, setColumns] = useState<Column[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formTitle, setFormTitle] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formPriority, setFormPriority] = useState<Priority>("Medium");
  const [formPrompt, setFormPrompt] = useState("");

  const fetchColumns = useCallback(async () => {
    try {
      const res = await fetch("/api/taskboard/columns");
      const data = await res.json();
      if (data.columns) {
        setColumns(data.columns);
      }
    } catch {
      // silently ignore fetch errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchColumns();
  }, [fetchColumns]);

  // Listen for SSE taskboard-update events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.type === "taskboard-update") {
        fetchColumns();
      }
    };
    window.addEventListener("opentool-ipc", handler);
    return () => window.removeEventListener("opentool-ipc", handler);
  }, [fetchColumns]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) return;

    await fetch("/api/taskboard", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: formTitle,
        description: formDesc,
        priority: formPriority.toLowerCase(),
        prompt: formPrompt,
      }),
    });

    setFormTitle("");
    setFormDesc("");
    setFormPriority("Medium");
    setFormPrompt("");
    setShowCreate(false);
    fetchColumns();
  };

  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    await fetch(`/api/taskboard/${taskId}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchColumns();
  };

  const totalTasks = columns.reduce((sum, c) => sum + c.count, 0);

  // Find the expanded task across all columns
  const expandedTask = expandedId
    ? columns.flatMap((c) => c.tasks).find((t) => t.id === expandedId) ?? null
    : null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>
            <span style={styles.titleIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#50fa7b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
            </span>
            Task Board
          </h2>
          <span style={styles.taskCount}>{totalTasks} tasks</span>
        </div>
        <button style={styles.createBtn} onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? "Cancel" : "+ New Task"}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} style={styles.createForm}>
          <div style={styles.formRow}>
            <input
              style={styles.input}
              placeholder="Task title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              autoFocus
            />
            <select
              style={styles.select}
              value={formPriority}
              onChange={(e) => setFormPriority(e.target.value as Priority)}
            >
              {PRIORITY_ORDER.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <textarea
            style={styles.textarea}
            placeholder="Description (optional)"
            value={formDesc}
            onChange={(e) => setFormDesc(e.target.value)}
            rows={2}
          />
          <textarea
            style={styles.textarea}
            placeholder="Agent prompt (what should the agent do?)"
            value={formPrompt}
            onChange={(e) => setFormPrompt(e.target.value)}
            rows={2}
          />
          <button type="submit" style={styles.submitBtn}>
            Create Task
          </button>
        </form>
      )}

      {/* Kanban columns */}
      {loading ? (
        <div style={styles.loading}>Loading task board...</div>
      ) : (
        <div style={styles.board}>
          {KANBAN_COLUMNS.map(({ status, label }) => {
            const col = columns.find((c) => c.status === status);
            const tasks = col?.tasks ?? [];
            return (
              <div key={status} style={styles.column}>
                <div style={styles.columnHeader}>
                  <span style={styles.columnLabel}>{label}</span>
                  <span style={styles.columnCount}>{tasks.length}</span>
                </div>
                <div style={styles.columnBody}>
                  {tasks.length === 0 && (
                    <div style={styles.emptyColumn}>No tasks</div>
                  )}
                  {tasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      expanded={expandedId === task.id}
                      onToggle={() =>
                        setExpandedId(expandedId === task.id ? null : task.id)
                      }
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Expanded task detail overlay */}
      {expandedTask && (
        <TaskDetailOverlay
          task={expandedTask}
          onClose={() => setExpandedId(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}

// ── Task Card ─────────────────────────────────────────────────

function TaskCard({
  task,
  expanded,
  onToggle,
  onStatusChange,
}: {
  task: AgentTask;
  expanded: boolean;
  onToggle: () => void;
  onStatusChange: (id: string, status: TaskStatus) => void;
}) {
  const transitions = STATUS_TRANSITIONS[task.status] ?? [];

  return (
    <div
      style={{
        ...styles.card,
        borderLeft: `3px solid ${PRIORITY_COLORS[task.priority]}`,
      }}
      onClick={onToggle}
    >
      <div style={styles.cardTop}>
        <span style={styles.cardTitle}>{task.title}</span>
        <span
          style={{
            ...styles.priorityBadge,
            backgroundColor: PRIORITY_COLORS[task.priority] + "22",
            color: PRIORITY_COLORS[task.priority],
          }}
        >
          {task.priority}
        </span>
      </div>
      <div style={styles.cardMeta}>
        <span style={styles.assigneeBadge}>{assigneeDisplay(task.assignee)}</span>
        <span style={styles.timeAgo}>{timeAgo(task.updated_at)}</span>
      </div>
      {expanded && (
        <div style={styles.cardExpanded} onClick={(e) => e.stopPropagation()}>
          {task.description && (
            <p style={styles.cardDesc}>{task.description}</p>
          )}
          {task.prompt && (
            <div style={styles.cardSection}>
              <span style={styles.cardSectionLabel}>Prompt</span>
              <pre style={styles.cardPre}>{task.prompt}</pre>
            </div>
          )}
          {task.result && (
            <div style={styles.cardSection}>
              <span style={styles.cardSectionLabel}>Result</span>
              <pre style={styles.cardPre}>{task.result}</pre>
            </div>
          )}
          <div style={styles.transitionRow}>
            {transitions.map((s) => (
              <button
                key={s}
                style={styles.transitionBtn}
                onClick={() => onStatusChange(task.id, s)}
              >
                {statusLabel(s)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Task Detail Overlay ───────────────────────────────────────

function TaskDetailOverlay({
  task,
  onClose,
  onStatusChange,
}: {
  task: AgentTask;
  onClose: () => void;
  onStatusChange: (id: string, status: TaskStatus) => void;
}) {
  const transitions = STATUS_TRANSITIONS[task.status] ?? [];

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.overlayPanel} onClick={(e) => e.stopPropagation()}>
        <div style={styles.overlayHeader}>
          <h3 style={styles.overlayTitle}>{task.title}</h3>
          <button style={styles.overlayClose} onClick={onClose}>
            x
          </button>
        </div>

        <div style={styles.overlayMeta}>
          <span
            style={{
              ...styles.priorityBadge,
              backgroundColor: PRIORITY_COLORS[task.priority] + "22",
              color: PRIORITY_COLORS[task.priority],
            }}
          >
            {task.priority}
          </span>
          <span style={styles.assigneeBadge}>{assigneeLabel(task.assignee)}</span>
          <span style={styles.statusBadge}>{statusLabel(task.status)}</span>
          <span style={styles.timeAgo}>{timeAgo(task.created_at)}</span>
        </div>

        {task.description && (
          <div style={styles.overlaySection}>
            <span style={styles.cardSectionLabel}>Description</span>
            <p style={styles.overlayText}>{task.description}</p>
          </div>
        )}

        {task.prompt && (
          <div style={styles.overlaySection}>
            <span style={styles.cardSectionLabel}>Agent Prompt</span>
            <pre style={styles.cardPre}>{task.prompt}</pre>
          </div>
        )}

        {task.result && (
          <div style={styles.overlaySection}>
            <span style={styles.cardSectionLabel}>Result</span>
            <pre style={styles.cardPre}>{task.result}</pre>
          </div>
        )}

        <div style={styles.overlayActions}>
          {transitions.map((s) => (
            <button
              key={s}
              style={styles.transitionBtn}
              onClick={() => {
                onStatusChange(task.id, s);
                onClose();
              }}
            >
              Move to {statusLabel(s)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Status label helper ───────────────────────────────────────

function statusLabel(s: TaskStatus): string {
  switch (s) {
    case "Backlog":
      return "Backlog";
    case "InProgress":
      return "In Progress";
    case "AwaitingReview":
      return "Awaiting Review";
    case "Approved":
      return "Approved";
    case "Done":
      return "Done";
    case "Failed":
      return "Failed";
  }
}

// ── Inline styles (dark terminal theme) ───────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: "24px",
    minHeight: "100%",
    backgroundColor: "#0d0d0d",
    color: "#f8f8f2",
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 600,
    color: "#f8f8f2",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  titleIcon: {
    display: "flex",
    alignItems: "center",
  },
  taskCount: {
    fontSize: "12px",
    color: "#6272a4",
    backgroundColor: "#1e1e2e",
    padding: "2px 8px",
    borderRadius: "4px",
  },
  createBtn: {
    background: "none",
    border: "1px solid rgba(255,255,255,0.06)",
    color: "#50fa7b",
    fontSize: "13px",
    padding: "6px 14px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  createForm: {
    backgroundColor: "#1a1b26",
    border: "1px solid #282a36",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
  },
  formRow: {
    display: "flex",
    gap: "10px",
  },
  input: {
    flex: 1,
    backgroundColor: "#1e1e2e",
    border: "1px solid #282a36",
    borderRadius: "4px",
    color: "#f8f8f2",
    padding: "8px 12px",
    fontSize: "13px",
    fontFamily: "inherit",
    outline: "none",
  },
  select: {
    backgroundColor: "#1e1e2e",
    border: "1px solid #282a36",
    borderRadius: "4px",
    color: "#f8f8f2",
    padding: "8px 12px",
    fontSize: "13px",
    fontFamily: "inherit",
    outline: "none",
    cursor: "pointer",
  },
  textarea: {
    backgroundColor: "#1e1e2e",
    border: "1px solid #282a36",
    borderRadius: "4px",
    color: "#f8f8f2",
    padding: "8px 12px",
    fontSize: "13px",
    fontFamily: "inherit",
    outline: "none",
    resize: "vertical" as const,
  },
  submitBtn: {
    alignSelf: "flex-end" as const,
    backgroundColor: "#50fa7b",
    border: "none",
    color: "#0d0d0d",
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  loading: {
    textAlign: "center" as const,
    padding: "60px",
    color: "#6272a4",
    fontSize: "14px",
  },
  board: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "16px",
    minHeight: "400px",
  },
  column: {
    backgroundColor: "#1a1b26",
    borderRadius: "8px",
    border: "1px solid #282a36",
    display: "flex",
    flexDirection: "column" as const,
    minHeight: "300px",
  },
  columnHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 14px",
    borderBottom: "1px solid #282a36",
  },
  columnLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#a8b2b8",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  columnCount: {
    fontSize: "11px",
    color: "#6272a4",
    backgroundColor: "#1e1e2e",
    padding: "1px 6px",
    borderRadius: "3px",
  },
  columnBody: {
    padding: "8px",
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    overflowY: "auto" as const,
  },
  emptyColumn: {
    textAlign: "center" as const,
    padding: "32px 12px",
    color: "#6272a4",
    fontSize: "12px",
    fontStyle: "italic" as const,
  },
  card: {
    backgroundColor: "#1e1e2e",
    borderRadius: "6px",
    padding: "10px 12px",
    cursor: "pointer",
    transition: "background-color 0.15s",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "6px",
  },
  cardTitle: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#f8f8f2",
    lineHeight: 1.4,
  },
  priorityBadge: {
    fontSize: "10px",
    fontWeight: 600,
    padding: "2px 6px",
    borderRadius: "3px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
  },
  cardMeta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  },
  assigneeBadge: {
    fontSize: "11px",
    color: "#6272a4",
    backgroundColor: "#1a1b26",
    padding: "1px 6px",
    borderRadius: "3px",
  },
  timeAgo: {
    fontSize: "11px",
    color: "#6272a4",
  },
  cardExpanded: {
    marginTop: "10px",
    paddingTop: "10px",
    borderTop: "1px solid #282a36",
  },
  cardDesc: {
    margin: "0 0 8px",
    fontSize: "12px",
    color: "#a8b2b8",
    lineHeight: 1.5,
  },
  cardSection: {
    marginBottom: "8px",
  },
  cardSectionLabel: {
    display: "block",
    fontSize: "10px",
    fontWeight: 600,
    color: "#6272a4",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: "4px",
  },
  cardPre: {
    margin: 0,
    backgroundColor: "#0d0d0d",
    border: "1px solid #282a36",
    borderRadius: "4px",
    padding: "8px",
    fontSize: "12px",
    color: "#50fa7b",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    fontFamily: "inherit",
    maxHeight: "120px",
    overflowY: "auto" as const,
  },
  transitionRow: {
    display: "flex",
    gap: "6px",
    marginTop: "8px",
    flexWrap: "wrap" as const,
  },
  transitionBtn: {
    background: "none",
    border: "1px solid rgba(255,255,255,0.06)",
    color: "#50fa7b",
    fontSize: "11px",
    padding: "4px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  // Overlay
  overlay: {
    position: "fixed" as const,
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  overlayPanel: {
    backgroundColor: "#1a1b26",
    border: "1px solid #282a36",
    borderRadius: "10px",
    width: "540px",
    maxWidth: "90vw",
    maxHeight: "80vh",
    overflowY: "auto" as const,
    padding: "24px",
  },
  overlayHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "16px",
  },
  overlayTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 600,
    color: "#f8f8f2",
  },
  overlayClose: {
    background: "none",
    border: "none",
    color: "#6272a4",
    fontSize: "16px",
    cursor: "pointer",
    padding: "0 4px",
    fontFamily: "inherit",
  },
  overlayMeta: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap" as const,
  },
  statusBadge: {
    fontSize: "11px",
    color: "#50fa7b",
    backgroundColor: "#50fa7b15",
    padding: "2px 8px",
    borderRadius: "3px",
  },
  overlaySection: {
    marginBottom: "16px",
  },
  overlayText: {
    margin: 0,
    fontSize: "13px",
    color: "#a8b2b8",
    lineHeight: 1.6,
  },
  overlayActions: {
    display: "flex",
    gap: "8px",
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #282a36",
    flexWrap: "wrap" as const,
  },
};
